import type { AppBaseTableDispositionInput } from '@affine/graphql';
import type {
  CalendarReconcileColumns,
  CalendarReconcileSnapshot,
} from '@blocksuite/affine/data-view';

import type { ViewBackedAppKind } from '../streamlain/view-backed-app/types';

export type OfflineMutationStatus = 'pending' | 'flushing' | 'failed';

/** How long a deletion waits before it may be sent, so it can be undone. */
export const OFFLINE_DELETION_HOLD_MS = 5000;

export type ViewBackedAppAttachSourceOperation = `${string}.attachSource`;
export type ViewBackedAppCreateSourceOperation = `${string}.createSource`;
export type ViewBackedAppUpdateSourcePreferenceOperation =
  `${string}.updateSourcePreference`;
export type ViewBackedAppSourceMutationOperation =
  | ViewBackedAppAttachSourceOperation
  | ViewBackedAppCreateSourceOperation
  | ViewBackedAppUpdateSourcePreferenceOperation;

export type OfflineMutationOperation =
  | 'doc.permanentDelete'
  | 'doc.trash'
  | 'doc.restore'
  | 'appBase.trash'
  | 'appBase.restore'
  | 'appBase.permanentDelete'
  | 'automation.trash'
  | 'automation.restore'
  | 'automation.permanentDelete'
  | 'blob.upload'
  | 'calendar.attachSource'
  | 'calendar.createMailboxLink'
  | 'calendar.createSource'
  | 'calendar.deleteMailboxLink'
  | 'calendar.reconcileEvent'
  | 'calendar.respondInvitation'
  | 'calendar.updateMailboxLink'
  | 'calendar.updateSourcePreference'
  | ViewBackedAppSourceMutationOperation
  | 'database.move'
  | 'database.trash'
  | 'database.restore'
  | 'database.permanentDelete'
  | 'folder.trash'
  | 'folder.restore'
  | 'folder.permanentDelete'
  | 'folder.create'
  | 'folder.rename'
  | 'folder.move'
  | 'folder.addDoc'
  | 'folder.addDocToRoot'
  | 'folder.removeDoc'
  | 'permission.grant'
  | 'permission.update'
  | 'permission.revoke'
  | 'mail.updateMessageState'
  | 'mail.moveMessages'
  | 'mail.createFolder'
  | 'mail.updateFolder'
  | 'mail.deleteFolder'
  | 'mail.send';

export interface DocLifecycleMutationPayload {
  docId: string;
}
export interface AppBaseLifecycleMutationPayload {
  appBaseId: string;
}

export interface PermissionGraphMutationMetadata {
  affectedFolderIds?: string[];
  affectedDocIds?: string[];
  expectedPermissionGraphVersion?: number;
  optimisticPatchId?: string;
  rollback?: {
    folderIds?: string[];
    docIds?: string[];
  };
}

export interface FolderTrashMutationPayload extends PermissionGraphMutationMetadata {
  folderId: string;
}

export interface FolderCreateMutationPayload extends PermissionGraphMutationMetadata {
  folderId: string;
  name: string;
  parentId: string | null;
  visibility?: string;
  inheritPermissions?: boolean;
}

export interface FolderRenameMutationPayload extends PermissionGraphMutationMetadata {
  folderId: string;
  name: string;
}

export interface FolderMoveMutationPayload extends PermissionGraphMutationMetadata {
  folderId: string;
  parentId: string | null;
}

export interface FolderAddDocMutationPayload extends PermissionGraphMutationMetadata {
  folderId: string;
  docId: string;
}

export interface FolderAddDocToRootMutationPayload extends PermissionGraphMutationMetadata {
  docId: string;
}

export interface FolderRemoveDocMutationPayload extends PermissionGraphMutationMetadata {
  docId: string;
}

export type ObjectPermissionTargetKind = 'doc' | 'folder';
export type ObjectPermissionPrincipalKind = 'group' | 'mate' | 'user';

export interface ObjectPermissionTargetRef {
  id: string;
  kind: ObjectPermissionTargetKind;
}

export interface ObjectPermissionPrincipalRef {
  id: string;
  kind: ObjectPermissionPrincipalKind;
}

export interface PermissionMutationPayload extends PermissionGraphMutationMetadata {
  principal: ObjectPermissionPrincipalRef;
  role?: string;
  target: ObjectPermissionTargetRef;
}

export type PermissionGrantMutationPayload = PermissionMutationPayload & {
  role: string;
};

export type PermissionUpdateMutationPayload = PermissionMutationPayload & {
  role: string;
};

export type PermissionRevokeMutationPayload = PermissionMutationPayload;

export interface MailUpdateMessageStateMutationPayload {
  input: {
    workspaceId: string;
    messageId: string;
    isRead?: boolean | null;
    isFlagged?: boolean | null;
    isStarred?: boolean | null;
  };
}

export interface MailMoveMessagesMutationPayload {
  workspaceId: string;
  messageIds: string[];
  folderId: string;
}

export interface MailCreateFolderMutationPayload {
  input: {
    workspaceId: string;
    accountId: string;
    displayName: string;
    parentFolderId?: string | null;
    path?: string | null;
    parentPath?: string | null;
  };
}

export interface MailUpdateFolderMutationPayload {
  input: {
    workspaceId: string;
    folderId: string;
    displayName?: string | null;
  };
}

export interface MailDeleteFolderMutationPayload {
  workspaceId: string;
  folderId: string;
}

export interface MailSendMutationPayload {
  input: {
    workspaceId: string;
    accountId: string;
    idempotencyKey: string;
    /** Server draft the compose already saved; the replay fills and sends it. */
    draftMessageId?: string | null;
    composeKind?: string | null;
    sourceMessageId?: string | null;
    inReplyToHeader?: string | null;
    referencesHeader?: string | null;
    signatureId?: string | null;
    to: string[];
    cc?: string[] | null;
    bcc?: string[] | null;
    subject?: string | null;
    bodyText?: string | null;
    bodyHtml?: string | null;
    attachments?: Array<{
      blobKey: string;
      filename: string;
      mimeType?: string | null;
    }> | null;
  };
}

/**
 * One trash or restore of a table under its own server operation. The
 * operation id stays the same for every retry of this intent.
 */
export interface DatabaseMoveMutationPayload {
  operationId: string;
  databaseId: string;
  source: { docId: string; blockId: string };
  target: {
    docId: string;
    parentBlockId: string;
    index: number;
    xywh?: string;
  };
  impactsConfirmed: boolean;
}

export interface DatabaseLifecycleMutationPayload {
  docId: string;
  blockId: string;
  databaseId: string;
  operationId: string;
  /** Whether the table is hidden while queued; a restore always keeps it. */
  localVisibility: 'hide' | 'keep';
}

export interface AutomationLifecycleMutationPayload {
  docId: string;
  blockId: string;
  operationId: string;
  /** Trash only: false while the copy question still has to be asked. */
  copyConfirmed?: boolean;
  /** Trash only: the name the deferred copy question shows. */
  displayName?: string;
}

export interface BlobUploadMutationPayload {
  workspaceId: string;
  blobKey: string;
  docId?: string;
  mimeType: string;
  size: number;
}

export interface ViewBackedAppSourceRefMutationPayload {
  appKind: ViewBackedAppKind;
  blockId: string;
  databaseId: string;
  docId: string;
  viewId: string;
  viewMode: string;
}

export interface ViewBackedAppAttachSourceMutationPayload {
  areaRootFolderId?: string | null;
  blockId: string;
  colorOverride?: string | null;
  databaseId: string;
  docId: string;
  title?: string | null;
  viewId: string;
  visible: boolean;
}

export type ViewBackedAppCreateSourceMutationPayload =
  ViewBackedAppAttachSourceMutationPayload;

export interface ViewBackedAppUpdateSourcePreferenceMutationPayload {
  patch: {
    colorOverride?: string | null;
    titleOverride?: string | null;
    visible?: boolean;
  };
  sourceId: string;
}

export type CalendarSourceRefMutationPayload =
  ViewBackedAppSourceRefMutationPayload;

export type CalendarAttachSourceMutationPayload =
  ViewBackedAppAttachSourceMutationPayload;

export type CalendarCreateSourceMutationPayload =
  ViewBackedAppCreateSourceMutationPayload;

export type CalendarUpdateSourcePreferenceMutationPayload =
  ViewBackedAppUpdateSourcePreferenceMutationPayload;

export interface CalendarCreateMailboxLinkMutationPayload {
  input: {
    inboundEnabled: boolean;
    mailAccountId: string;
    outboundEnabled: boolean;
  };
  source: CalendarSourceRefMutationPayload;
}

export interface CalendarUpdateMailboxLinkMutationPayload {
  linkId: string;
  patch: {
    inboundEnabled?: boolean;
    outboundEnabled?: boolean;
    status?: 'active' | 'disabled';
  };
  source: CalendarSourceRefMutationPayload;
}

export interface CalendarDeleteMailboxLinkMutationPayload {
  linkId: string;
  source: CalendarSourceRefMutationPayload;
}

export interface CalendarRespondInvitationMutationPayload {
  icsSequence?: number | null;
  requestId: string;
  rowId: string;
  source: CalendarSourceRefMutationPayload;
  status: 'accepted' | 'declined' | 'tentative';
}

export interface CalendarReconcileEventMutationPayload {
  columns: CalendarReconcileColumns;
  databaseId: string;
  docId: string;
  previousEvent: CalendarReconcileSnapshot | null;
  requestId: string;
  rowId: string;
  skipMail: boolean;
  syncDocIds: string[];
  viewId: string;
}

export type OfflineMutationPayloadByOperation = {
  'doc.permanentDelete': DocLifecycleMutationPayload & {
    copyConfirmed: boolean;
  };
  'doc.trash': DocLifecycleMutationPayload;
  'doc.restore': DocLifecycleMutationPayload;
  'appBase.trash': AppBaseLifecycleMutationPayload;
  'appBase.restore': AppBaseLifecycleMutationPayload;
  'appBase.permanentDelete': AppBaseLifecycleMutationPayload & {
    copyConfirmed: boolean;
    /**
     * One choice per table the container owns. A record without it (queued
     * before the question or by an older client) is refused by the server
     * with APP_BASE_TABLE_CHOICE_MISSING and asks the question again.
     */
    tables?: AppBaseTableDispositionInput[];
  };
  'automation.trash': AutomationLifecycleMutationPayload;
  'automation.restore': AutomationLifecycleMutationPayload;
  'automation.permanentDelete': AutomationLifecycleMutationPayload;
  'blob.upload': BlobUploadMutationPayload;
  'calendar.attachSource': ViewBackedAppAttachSourceMutationPayload;
  'calendar.createMailboxLink': CalendarCreateMailboxLinkMutationPayload;
  'calendar.createSource': ViewBackedAppCreateSourceMutationPayload;
  'calendar.deleteMailboxLink': CalendarDeleteMailboxLinkMutationPayload;
  'calendar.reconcileEvent': CalendarReconcileEventMutationPayload;
  'calendar.respondInvitation': CalendarRespondInvitationMutationPayload;
  'calendar.updateMailboxLink': CalendarUpdateMailboxLinkMutationPayload;
  'calendar.updateSourcePreference': ViewBackedAppUpdateSourcePreferenceMutationPayload;
  'database.move': DatabaseMoveMutationPayload;
  'database.trash': DatabaseLifecycleMutationPayload;
  'database.restore': DatabaseLifecycleMutationPayload;
  'database.permanentDelete': {
    docId: string;
    blockId: string;
  };
  'folder.trash': FolderTrashMutationPayload;
  'folder.restore': FolderTrashMutationPayload;
  'folder.permanentDelete': FolderTrashMutationPayload & {
    copyConfirmed: boolean;
  };
  'folder.create': FolderCreateMutationPayload;
  'folder.rename': FolderRenameMutationPayload;
  'folder.move': FolderMoveMutationPayload;
  'folder.addDoc': FolderAddDocMutationPayload;
  'folder.addDocToRoot': FolderAddDocToRootMutationPayload;
  'folder.removeDoc': FolderRemoveDocMutationPayload;
  'permission.grant': PermissionGrantMutationPayload;
  'permission.update': PermissionUpdateMutationPayload;
  'permission.revoke': PermissionRevokeMutationPayload;
  'mail.updateMessageState': MailUpdateMessageStateMutationPayload;
  'mail.moveMessages': MailMoveMessagesMutationPayload;
  'mail.createFolder': MailCreateFolderMutationPayload;
  'mail.updateFolder': MailUpdateFolderMutationPayload;
  'mail.deleteFolder': MailDeleteFolderMutationPayload;
  'mail.send': MailSendMutationPayload;
} & {
  [K in ViewBackedAppAttachSourceOperation]: ViewBackedAppAttachSourceMutationPayload;
} & {
  [K in ViewBackedAppCreateSourceOperation]: ViewBackedAppCreateSourceMutationPayload;
} & {
  [K in ViewBackedAppUpdateSourcePreferenceOperation]: ViewBackedAppUpdateSourcePreferenceMutationPayload;
};

export type OfflineMutationPayload<
  Operation extends OfflineMutationOperation = OfflineMutationOperation,
> = OfflineMutationPayloadByOperation[Operation];

export interface OfflineMutationScope {
  workspaceId: string;
  cacheOwnerKey: string;
}

export interface OfflineMutationRecord<
  Operation extends OfflineMutationOperation = OfflineMutationOperation,
> extends OfflineMutationScope {
  id: string;
  scopeKey: string;
  operation: Operation;
  payload: OfflineMutationPayload<Operation>;
  idempotencyKey: string;
  status: OfflineMutationStatus;
  attempts: number;
  createdAt: number;
  sequence?: number;
  updatedAt: number;
  lastError?: string;
  /**
   * Why a failed mutation stopped: the server refused it, or every retry of a
   * temporary error was used up. Set only while the status is failed.
   */
  failure?: OfflineMutationFailure;
  /** Not claimable before this time; set only for a mutation with a hold. */
  claimableAt?: number;
  /**
   * When the first claim handed the mutation to its executor. It survives
   * retries, so a mutation once sent can no longer be canceled.
   */
  sentAt?: number;
  /**
   * Mutations of the same target share this key and are claimed only in the
   * order they were queued; a held earlier one holds back the later ones.
   */
  orderKey?: string;
}

export type OfflineMutationFailure = 'refused' | 'retries-exhausted';

export type OfflineMutationInput<
  Operation extends OfflineMutationOperation = OfflineMutationOperation,
> = {
  operation: Operation;
  payload: OfflineMutationPayload<Operation>;
  idempotencyKey: string;
  dedupePending?: boolean;
  id?: string;
  /** Delay before the mutation may be sent; until then it can be canceled. */
  holdMs?: number;
  /** Keeps the queue order against other mutations of the same target. */
  orderKey?: string;
};

export type OfflineMutationExecutor<
  Operation extends OfflineMutationOperation = OfflineMutationOperation,
> = (mutation: OfflineMutationRecord<Operation>) => Promise<void>;

export interface OfflineMutationOutboxStorage {
  listPending(scope: OfflineMutationScope): Promise<OfflineMutationRecord[]>;
  listFailed(scope: OfflineMutationScope): Promise<OfflineMutationRecord[]>;
  listAll(scope: OfflineMutationScope): Promise<OfflineMutationRecord[]>;
  /**
   * In one transaction: the pending or flushing record of the scope with the
   * same idempotency key, or the given record written as new.
   */
  insertPendingIfAbsent(
    record: OfflineMutationRecord
  ): Promise<{ record: OfflineMutationRecord; inserted: boolean }>;
  /**
   * In one transaction: marks the record flushing and returns it, or null when
   * it is gone, failed or still held. A record already flushing (after a
   * reload) is returned unchanged.
   */
  claim(id: string, now: number): Promise<OfflineMutationRecord | null>;
  /** In one transaction: removes the record only if it was never sent. */
  removeIfUnsent(id: string): Promise<boolean>;
  upsert(mutation: OfflineMutationRecord): Promise<void>;
  update(mutation: OfflineMutationRecord): Promise<void>;
  remove(id: string): Promise<void>;
  clearScope(scope: OfflineMutationScope): Promise<void>;
}

export function offlineMutationScopeKey(scope: OfflineMutationScope): string {
  return `${scope.workspaceId}:${scope.cacheOwnerKey}`;
}
